import type { NoteStatus } from '@/lib/api/product-lot-api'
import { dateFilter } from '@/components/common/filters/date-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { m } from '@/lib/paraglide/messages'

/**
 * Preset cho nhóm màn phiếu kho (PNK / PXK / PĐC / phiếu rút…).
 *
 * 15 màn phiếu dùng chung đúng ba thứ: trạng thái duyệt, "từ ngày", "đến ngày".
 * Khai lại ở từng màn nghĩa là 15 bản sao của cùng một danh sách trạng thái —
 * đổi nhãn một chỗ là sót 14 chỗ. Preset chỉ gói phần LẶP LẠI; "lý do" khác
 * nhau theo từng loại phiếu nên vẫn khai tại màn bằng `selectFilter`.
 *
 * Đây không phải tầng mới: mỗi hàm dưới đây chỉ gọi factory có sẵn và trả về
 * đúng `FilterFieldDef`, nên core vẫn không biết preset tồn tại.
 */

/** Trạng thái duyệt phiếu — `'all'` là mặc định, không ghi vào URL. */
export function noteStatusFilter() {
    return selectFilter<NoteStatus | 'all'>({
        label: m.sl_note_filter_status,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.sl_note_filter_all_status() },
            { value: 'pending', label: m.sl_note_status_pending() },
            { value: 'approved', label: m.sl_note_status_approved() },
            { value: 'rejected', label: m.sl_note_status_rejected() },
            { value: 'cancelled', label: m.sl_note_status_cancelled() },
        ],
    })
}

/**
 * Trạng thái phiếu lấy hàng (mở / đã đóng / đã huỷ) — khác hẳn trạng thái duyệt
 * của phiếu nhập-xuất. Bốn kho (bao bì, lấy hương, phân loại, chè mua) đều dùng
 * đúng tập này và cùng bộ nhãn, nên gom về một chỗ.
 *
 * Kiểu khai tại chỗ thay vì import: mỗi api module tự export một
 * `WithdrawalNoteStatus` riêng nhưng nội dung y hệt, import một cái sẽ khiến
 * ba màn còn lại phải ép kiểu.
 */
export function withdrawalStatusFilter() {
    return selectFilter<'all' | 'open' | 'closed' | 'cancelled'>({
        label: m.sl_note_filter_status,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.sl_note_filter_all_status() },
            { value: 'open', label: m.scl_withdrawal_status_open() },
            { value: 'closed', label: m.scl_withdrawal_status_closed() },
            { value: 'cancelled', label: m.scl_withdrawal_status_cancelled() },
        ],
    })
}

/** Mốc đầu khoảng ngày tạo phiếu. */
export function noteFromDateFilter() {
    return dateFilter({ label: m.sl_note_filter_from_date })
}

/** Mốc cuối khoảng ngày tạo phiếu. */
export function noteToDateFilter() {
    return dateFilter({ label: m.sl_note_filter_to_date })
}

/**
 * Ô tìm kiếm chung của danh sách phiếu (M-149). BE khớp mã phiếu hoặc số lô /
 * tên / SKU sản phẩm trên phiếu, bỏ dấu, khớp một phần — tham số `search`.
 */
export function noteSearchFilter() {
    return textFilter({
        label: m.sl_note_search_label,
        placeholder: m.sl_note_search_placeholder,
    })
}
